<template>
    <div class="media-draggable">
        <div class="tw:flex tw:flex-col tw:xl:flex-row tw:w-full">
            <div class="level-item">
                <div
                    v-if="!hasPrerequisite && !isAPrerequisite"
                    class="media-draggable__draghandle"
                    :class="{ 'is-disabled': disabled }"
                    @focus="hoverIconHidden = true"
                    @focusout="hoverIconHidden = false"
                    @mouseover="hoverIconHidden = true"
                    @mouseleave="hoverIconHidden = false"
                >
                    <e-icon v-if="hoverIcon && !hoverIconHidden" :icon="hoverIcon" />
                    <e-icon v-if="!hoverIcon || hoverIconHidden" icon="draggabledots" />
                </div>

                <div v-else class="is-disabled">
                    <e-tooltip
                        label="Dragging is disabled. Remove prerequisite to enable dragging"
                        position="is-right"
                        type="is-warning"
                        multilined
                    >
                        <e-icon icon="lock" />
                    </e-tooltip>
                </div>
            </div>

            <div
                class="level-item media-draggable__title tw:flex tw:flex-col"
                :class="{ 'is-clickable': canOpen }"
                @click="toggle"
                @keyup="toggle"
            >
                <div class="tw:flex">
                    <slot name="title" />
                    <e-icon
                        v-if="canOpen"
                        :icon="`angle-${open ? 'up' : 'down'}`"
                        class="has-fast-transition has-fast-transition-on-hover media-draggable__icon"
                    />
                </div>
            </div>

            <slot name="actions" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'MediaDraggable',

    data() {
        return {
            hoverIconHidden: false,
        };
    },

    props: {
        toggleCallback: {
            type: Function,
            default: () => null,
        },
        hoverIcon: {
            type: String,
            default: null,
        },
        open: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        canOpen: {
            type: Boolean,
            default: true,
        },
        hasPrerequisite: {
            type: Boolean,
            default: false,
        },
        isAPrerequisite: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        dragClasses() {
            return {
                'media-draggable__draghandle': !this.hasPrerequisite,
                'is-disabled': this.disabled,
            };
        },
    },

    methods: {
        toggle() {
            if (!this.canOpen) return;

            this.toggleCallback();
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.media-draggable {
    position: relative;

    .level {
        justify-content: flex-start;

        @include until($tablet) {
            display: flex;
            flex-direction: column;
        }

        .level-item {
            flex-grow: 0 !important;
        }
    }

    &:hover,
    &:active {
        color: $primary;
        transition: $transition-fast;
    }

    &__draghandle {
        font-size: $size-4;
        margin-top: -$size-10;
        transition: $transition-fast;

        @include from($tablet) {
            margin-top: 0;
            padding-right: $size-2;
        }

        &:hover {
            cursor: grab;
        }

        &:active {
            cursor: grabbing;
        }

        &::after {
            background: $border;
            content: '';
            display: block;
            height: 1px;
            position: absolute;
            left: 0;
            right: 0;
            top: calc(#{$card-header-padding-horizontal * 1.3} + 24px);
            width: 100%;

            @include from($tablet) {
                background: $border;
                content: '';
                display: block;
                height: 100%;
                left: calc(#{$card-header-padding-horizontal * 2} + 24px);
                position: absolute;
                right: auto;
                top: 0;
                width: 1px;
            }
        }
    }

    &__icon {
        margin-left: 0.5rem;
        vertical-align: middle;
    }

    &__title {
        flex-grow: 2;
        text-align: center;
        text-overflow: ellipsis;
        transition: $transition-fast;

        @include from($tablet) {
            margin-right: auto !important;
            text-align: left;
        }

        &:hover {
            color: $primary;
            transition: $transition-fast;
        }

        & > .capitalize {
            text-transform: capitalize;
        }

        .icon {
            margin-right: $size-9;
        }

        & + .level-item {
            @include from($tablet) {
                margin-left: 0 !important;
            }
        }
    }

    &__actions {
        display: inherit;
        align-items: center;
    }
}
</style>
